import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingCart, Minus, Plus, Trash2, Landmark, Banknote, Truck, Store, MailWarning, Loader2 } from "lucide-react";
import { api, errMsg, fmt } from "../lib/api";
import { LegalAccept } from "../components/LegalAccept";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { CartTotals } from "../components/RightRail";
import { DISTRICTS } from "../lib/categories";
import LocationPicker from "../map/LocationPicker";
import { DemoBadge, Empty, Field, PageTitle, btnPrimary, btnGhost, inputCls } from "../components/ui";

export function CartPage() {
  const { items, setQty, remove, quote } = useCart();
  if (!items.length) return <Empty icon={ShoppingCart} title="O seu carrinho está vazio" text="Explore o catálogo e compare preços entre lojas."
    action={<Link to="/catalogo" className={btnPrimary} data-testid="empty-cart-shop-btn">Ver produtos</Link>} />;
  return (
    <div data-testid="cart-page">
      <PageTitle title="Carrinho" sub={`${items.reduce((s, i) => s + i.qty, 0)} ${items.reduce((s, i) => s + i.qty, 0) === 1 ? "artigo" : "artigos"}`} />
      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        <div className="bo-card divide-y divide-gray-100">
          {items.map((i) => (
            <div key={i.offer_id} className="flex items-center gap-4 p-4" data-testid={`cart-item-${i.offer_id}`}>
              <img src={i.image} alt="" className="h-16 w-16 rounded-xl object-cover" />
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 font-semibold">{i.name}{i.is_demo && <DemoBadge />}</p>
                <p className="text-xs text-gray-500">Vendido por {i.store_name} · {fmt(i.price)} / un.</p>
              </div>
              <div className="flex items-center gap-1 rounded-full border border-gray-200">
                <button onClick={() => setQty(i.offer_id, i.qty - 1)} className="p-2" data-testid={`cart-dec-${i.offer_id}`}><Minus className="h-3.5 w-3.5" /></button>
                <span className="w-6 text-center font-semibold" data-testid={`cart-qty-${i.offer_id}`}>{i.qty}</span>
                <button onClick={() => setQty(i.offer_id, i.qty + 1)} className="p-2" data-testid={`cart-inc-${i.offer_id}`}><Plus className="h-3.5 w-3.5" /></button>
              </div>
              <p className="w-28 text-right font-bold">{fmt(i.price * i.qty)}</p>
              <button onClick={() => remove(i.offer_id)} className="text-gray-400 hover:text-red-500" data-testid={`cart-remove-${i.offer_id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="bo-card h-fit p-5">
          <h3 className="font-bold">Resumo</h3>
          <CartTotals quote={quote} />
          <p className="mt-2 text-xs text-gray-500">A entrega é calculada por loja. Preços confirmados no servidor.</p>
          <Link to="/checkout" className={`${btnPrimary} mt-4 w-full`} data-testid="cart-checkout-btn">Finalizar compra</Link>
        </div>
      </div>
    </div>
  );
}

const Option = ({ active, disabled, onClick, icon: Icon, title, text, testid }) => (
  <button type="button" disabled={disabled} onClick={onClick} data-testid={testid}
    className={`flex items-start gap-3 rounded-2xl border-2 p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${active ? "border-bo-green bg-bo-green/5" : "border-gray-200 hover:border-bo-green/40"}`}>
    <Icon className={`mt-0.5 h-5 w-5 ${active ? "text-bo-green" : "text-gray-400"}`} />
    <span><span className="block font-semibold">{title}</span><span className="block text-xs text-gray-500">{text}</span></span>
  </button>
);

function VerifyNotice() {
  const resend = async () => {
    try { await api.post("/auth/resend-verification"); toast.success("Email de confirmação reenviado"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="verify-email-notice">
      <MailWarning className="h-5 w-5" />
      <span className="flex-1">Confirme o seu email para poder finalizar encomendas.</span>
      <button onClick={resend} className={btnGhost} data-testid="resend-verification-btn">Reenviar email</button>
    </div>
  );
}

export default function Checkout() {
  const { items, quote, deliveryMethod, setDeliveryMethod, setDistrict, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [opts, setOpts] = useState(null);
  const [pay, setPay] = useState("");
  const [busy, setBusy] = useState(false);
  const a = user?.address || {};
  const [addr, setAddr] = useState({ full_name: a.full_name || user?.name || "", phone: a.phone || user?.phone || "",
    district: a.district || DISTRICTS[0], address_line: a.address_line || "", notes: a.notes || "", landmark: a.landmark || "", location: a.location || null });
  useEffect(() => { setDistrict(addr.district); }, [addr.district, setDistrict]);
  useEffect(() => {
    api.get("/payment-options").then(({ data }) => {
      setOpts(data);
      setPay(data.transfer_enabled ? "transfer" : data.cod_enabled ? "cash_on_delivery" : "");
    });
  }, []);
  if (!items.length) return <Empty icon={ShoppingCart} title="Nada para finalizar" action={<Link to="/catalogo" className={btnPrimary}>Ver produtos</Link>} />;
  const set = (k) => (e) => setAddr({ ...addr, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/orders", { items: items.map(({ offer_id, qty }) => ({ offer_id, qty })),
        delivery_method: deliveryMethod, payment_method: pay, address: addr, accept_legal: true });
      clear();
      api.patch("/auth/me", { address: addr }).catch(() => {});
      toast.success(`Encomenda ${data.number} criada`);
      nav(`/conta/pedidos/${data.id}`);
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="checkout-page">
      <PageTitle title="Finalizar compra" />
      {!user.email_verified && <div className="mb-4"><VerifyNotice /></div>}
      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <div className="flex flex-col gap-5">
          <section className="bo-card p-5">
            <h2 className="mb-3 font-bold">1. Entrega</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <Option active={deliveryMethod === "delivery"} onClick={() => setDeliveryMethod("delivery")} icon={Truck} title="Entrega ao domicílio" text="Custo por zona de entrega ou por loja" testid="delivery-option-delivery" />
              <Option active={deliveryMethod === "pickup"} disabled={opts && !opts.pickup_enabled} onClick={() => setDeliveryMethod("pickup")} icon={Store} title="Levantamento na loja" text="Sem custo de entrega" testid="delivery-option-pickup" />
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Field label="Nome"><input required minLength={2} value={addr.full_name} onChange={set("full_name")} className={inputCls} data-testid="checkout-name-input" /></Field>
              <Field label="Telefone"><input required minLength={7} value={addr.phone} onChange={set("phone")} placeholder="+239 ..." className={inputCls} data-testid="checkout-phone-input" /></Field>
              <Field label="Distrito"><select value={addr.district} onChange={set("district")} className={inputCls} data-testid="checkout-district-select">{DISTRICTS.map((d) => <option key={d}>{d}</option>)}</select></Field>
              <Field label="Morada / ponto de referência"><input required minLength={3} value={addr.address_line} onChange={set("address_line")} className={inputCls} data-testid="checkout-address-input" /></Field>
              {deliveryMethod === "delivery" && <LocationPicker addr={addr} setAddr={setAddr} />}
              <div className="sm:col-span-2"><Field label="Notas (opcional)"><input value={addr.notes} onChange={set("notes")} maxLength={300} className={inputCls} data-testid="checkout-notes-input" /></Field></div>
            </div>
          </section>
          <section className="bo-card p-5">
            <h2 className="mb-3 font-bold">2. Pagamento</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <Option active={pay === "transfer"} disabled={!opts?.transfer_enabled} onClick={() => setPay("transfer")} icon={Landmark} title="Transferência bancária"
                text={opts?.transfer_enabled ? "Envie o comprovativo após a encomenda. Validação pelo administrador." : "Dados bancários a configurar"} testid="payment-option-transfer" />
              <Option active={pay === "cash_on_delivery"} disabled={!opts?.cod_enabled} onClick={() => setPay("cash_on_delivery")} icon={Banknote} title="Pagamento na entrega"
                text="Pague em numerário ao receber" testid="payment-option-cod" />
            </div>
            {opts && !opts.transfer_enabled && <p className="mt-3 text-xs text-amber-700" data-testid="bank-not-configured">Dados bancários a configurar: a transferência fica disponível quando o administrador ativar uma conta verificada.</p>}
          </section>
        </div>
        <aside className="bo-card h-fit p-5">
          <h3 className="font-bold">Resumo da encomenda</h3>
          <div className="mt-3 flex flex-col gap-2 text-sm">
            {(quote?.items || []).map((l) => <div key={l.offer_id} className="flex justify-between gap-2"><span className="truncate">{l.qty}× {l.name}</span><span className="shrink-0">{fmt(l.line_total)}</span></div>)}
          </div>
          <CartTotals quote={quote} />
          {quote?.delivery_source && <p className="mt-1 text-xs text-gray-500" data-testid="delivery-source">{{ zone: `Entrega por zona: ${addr.district}`, store: "Entrega calculada pelas lojas", pickup: "Levantamento na loja" }[quote.delivery_source]}</p>}
          <div className="mt-4"><LegalAccept context="checkout" /></div>
          <button disabled={busy || !pay || !user.email_verified || quote?.problems?.length > 0} className={`${btnPrimary} mt-4 w-full`} data-testid="place-order-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}Confirmar encomenda
          </button>
        </aside>
      </div>
    </form>
  );
}
